/**
 * Settings → Integrations, the platform half (IntegrationsViewModel, contracts apps.ts): Apps (owners and admins
 * create one — bot user, scopes, OAuth redirect URLs, interactivity / options load / events URLs, slash commands, workflow
 * steps (Slack's functions: callback id, title, description, inputs, outputs) — and see its credentials
 * once; per app: edit, show signing secret, regenerate bot token / client secret / signing secret, remove; apps
 * installed from another workspace can only be removed) and Outgoing webhooks (channels and/or trigger words, URL,
 * signing secret, last error).
 */
import { APP_EVENTS, APP_SCOPES, type IntegrationsState, type IntegrationsViewModel } from "@bundle/core";
import { MAX_APP_STEP_PARAMS, MAX_APP_WORKFLOW_STEPS, WorkflowVarType, type AppEventType, type AppScope, type AppWorkflowStep } from "@bundle/contracts";
import { useState } from "react";
import { Avatar, Banner, Button, Checkbox, Icon, IconButton, Menu, Select, Sheet, Stack, Text, TextField, type MenuEntry } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";
import { AppBadge } from "../Badges.tsx";
import { FieldNote, SettingsSection } from "./SettingsKit.tsx";

type T = ReturnType<typeof useT>;

export function scopeLabel(t: T, scope: AppScope): string {
  switch (scope) {
    case "chat:write":
      return t("integrations.scope.chatWrite");
    case "channels:read":
      return t("integrations.scope.channelsRead");
    case "channels:history":
      return t("integrations.scope.channelsHistory");
    case "users:read":
      return t("integrations.scope.usersRead");
    case "reactions:write":
      return t("integrations.scope.reactionsWrite");
    case "commands":
      return t("integrations.scope.commands");
    case "groups:read":
      return t("integrations.scope.groupsRead");
    case "groups:history":
      return t("integrations.scope.groupsHistory");
    case "im:history":
      return t("integrations.scope.imHistory");
    case "mpim:history":
      return t("integrations.scope.mpimHistory");
    case "app_mentions:read":
      return t("integrations.scope.appMentionsRead");
    case "reactions:read":
      return t("integrations.scope.reactionsRead");
  }
}

export function eventLabel(t: T, event: AppEventType): string {
  switch (event) {
    case "message.channels":
      return t("integrations.event.messageChannels");
    case "message.groups":
      return t("integrations.event.messageGroups");
    case "message.im":
      return t("integrations.event.messageIm");
    case "message.mpim":
      return t("integrations.event.messageMpim");
    case "app_mention":
      return t("integrations.event.appMention");
    case "reaction_added":
      return t("integrations.event.reactionAdded");
    case "reaction_removed":
      return t("integrations.event.reactionRemoved");
    case "member_joined_channel":
      return t("integrations.event.memberJoinedChannel");
    case "member_left_channel":
      return t("integrations.event.memberLeftChannel");
    case "channel_created":
      return t("integrations.event.channelCreated");
    case "channel_rename":
      return t("integrations.event.channelRename");
  }
}

function useCopy() {
  const [copied, setCopied] = useState<string | undefined>(undefined);
  const copy = (key: string, value: string) => {
    void navigator.clipboard?.writeText(value).then(
      () => setCopied(key),
      () => undefined,
    );
  };
  return { copied, copy, reset: () => setCopied(undefined) };
}

export function AppsSection({ vm, state: s }: { vm: IntegrationsViewModel; state: IntegrationsState }) {
  const t = useT();
  const tr = useTr();
  const { copied, copy, reset } = useCopy();
  if (!s.appsSupported) return null;
  const e = s.appEditor;
  const removing = s.apps.find((a) => a.app.id === s.confirmRemoveApp);
  const cred = s.credentials;
  const credLabel = (kind: string) =>
    kind === "botToken" ? t("integrations.app.botToken") : kind === "clientId" ? t("integrations.app.clientId") : kind === "clientSecret" ? t("integrations.app.clientSecret") : t("integrations.app.signingSecret");
  const closeCred = () => {
    reset();
    vm.dismissCredentials();
  };
  return (
    <>
      <SettingsSection title={t("integrations.apps.title")} description={t("integrations.apps.body")} testID="integrations-apps">
        {s.apps.length === 0 ? <FieldNote text={t("integrations.apps.empty")} testID="integrations-apps-empty" /> : null}
        <Stack gap="1">
          {s.apps.map(({ app: a, commandCount, loopWarning }) => {
            const menu: MenuEntry[] = a.external
              ? [{ id: "remove", label: t("integrations.app.remove"), icon: "trash", destructive: true, onSelect: () => vm.askRemoveApp(a.id) }]
              : [
                  { id: "edit", label: t("integrations.app.edit"), icon: "edit", onSelect: () => vm.openEditApp(a.id) },
                  { id: "signing", label: t("integrations.app.showSigningSecret"), icon: "eye", onSelect: () => vm.showSigningSecret(a.id) },
                  { id: "sep1", separator: true },
                  { id: "bot", label: t("integrations.app.rotateBotToken"), icon: "reload", onSelect: () => vm.rotateSecret(a.id, "botToken") },
                  { id: "client", label: t("integrations.app.rotateClientSecret"), icon: "reload", onSelect: () => vm.rotateSecret(a.id, "clientSecret") },
                  { id: "signingRotate", label: t("integrations.app.rotateSigningSecret"), icon: "reload", onSelect: () => vm.rotateSecret(a.id, "signingSecret") },
                  { id: "sep2", separator: true },
                  { id: "remove", label: t("integrations.app.remove"), icon: "trash", destructive: true, onSelect: () => vm.askRemoveApp(a.id) },
                ];
            return (
              <Stack key={a.id} direction="horizontal" align="center" gap="3" paddingY="2" testID={`app-${a.id}`}>
                <Avatar name={a.name} src={a.iconUrl} size="avatar.md" square />
                <Stack grow gap="0.5">
                  <Stack direction="horizontal" align="center" gap="2">
                    <Text weight="semibold" lines={1}>
                      {a.name}
                    </Text>
                    <AppBadge />
                  </Stack>
                  <Text variant="footnote" color="text.secondary" lines={2}>
                    {[
                      a.external ? t("integrations.app.external") : undefined,
                      t("integrations.app.meta", { scopes: t("integrations.app.scopeCount", { count: a.scopes.length }), commands: t("integrations.app.commandCount", { count: commandCount }) }),
                      a.commands.map((c) => c.command).join(" "),
                      a.description,
                    ]
                      .filter((x): x is string => x !== undefined && x !== "")
                      .join(" · ")}
                  </Text>
                  {loopWarning ? (
                    <Banner tone="warning" message={t("integrations.app.eventsLoopWarning")} testID={`app-${a.id}-loop-warning`} />
                  ) : null}
                </Stack>
                {s.canManageApps ? (
                  <Menu items={menu} align="end" testID={`app-${a.id}-menu`}>
                    <IconButton icon="more" accessibilityLabel={t("integrations.app.more")} onPress={() => undefined} testID={`app-${a.id}-more`} />
                  </Menu>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
        {s.canManageApps ? (
          <Stack direction="horizontal">
            <Button variant="secondary" icon="plus" label={t("integrations.app.create")} onPress={() => vm.openCreateApp()} testID="integrations-create-app" />
          </Stack>
        ) : (
          <FieldNote text={t("integrations.apps.adminsOnly")} testID="integrations-apps-admins" />
        )}
      </SettingsSection>

      <Sheet open={e !== undefined} onClose={() => vm.closeAppEditor()} title={t(e?.id === undefined ? "integrations.app.create" : "integrations.app.edit")} testID="app-sheet">
        {e === undefined ? null : (
          <Stack gap="3" padding="4">
            <TextField label={t("integrations.app.name")} value={e.name} onChange={(v) => vm.editApp({ name: v })} placeholder={t("integrations.app.namePlaceholder")} autoFocus testID="app-name" />
            <TextField label={t("integrations.app.description")} value={e.description} onChange={(v) => vm.editApp({ description: v })} testID="app-description" />
            <Stack gap="1">
              <Text variant="footnote" weight="semibold">
                {t("integrations.app.scopes")}
              </Text>
              {APP_SCOPES.map((scope) => (
                <Stack key={scope} direction="horizontal" align="center" gap="2">
                  <Checkbox label={scopeLabel(t, scope)} value={e.scopes.includes(scope)} onChange={() => vm.toggleScope(scope)} testID={`app-scope-${scope.replace(":", "-")}`} />
                  <Text variant="caption" color="text.tertiary" mono>
                    {scope}
                  </Text>
                </Stack>
              ))}
            </Stack>
            <TextField label={t("integrations.app.redirectUrls")} helperText={t("integrations.app.redirectUrlsHelp")} value={e.redirectUrls} onChange={(v) => vm.editApp({ redirectUrls: v })} multiline keyboard="url" testID="app-redirects" />
            <TextField label={t("integrations.app.interactivityUrl")} helperText={t("integrations.app.interactivityHelp")} value={e.interactivityUrl} onChange={(v) => vm.editApp({ interactivityUrl: v })} keyboard="url" testID="app-interactivity" />
            <TextField label={t("integrations.app.optionsUrl")} helperText={t("integrations.app.optionsHelp")} value={e.optionsUrl} onChange={(v) => vm.editApp({ optionsUrl: v })} keyboard="url" testID="app-options-url" />
            <TextField label={t("integrations.app.eventsUrl")} helperText={t("integrations.app.eventsHelp")} value={e.eventsUrl} onChange={(v) => vm.editApp({ eventsUrl: v })} keyboard="url" testID="app-events-url" />
            <Stack gap="1">
              <Text variant="footnote" weight="semibold">
                {t("integrations.app.events")}
              </Text>
              <Text variant="caption" color="text.secondary">
                {t("integrations.app.eventsSubscribeHelp")}
              </Text>
              {APP_EVENTS.map((event) => (
                <Stack key={event} direction="horizontal" align="center" gap="2">
                  <Checkbox label={eventLabel(t, event)} value={e.events.includes(event)} onChange={() => vm.toggleEvent(event)} testID={`app-event-${event.replace(/[._]/g, "-")}`} />
                  <Text variant="caption" color="text.tertiary" mono>
                    {event}
                  </Text>
                </Stack>
              ))}
            </Stack>
            <Stack gap="2">
              <Text variant="footnote" weight="semibold">
                {t("integrations.app.commands")}
              </Text>
              {e.commands.map((c, i) => (
                <Stack key={i} gap="2" padding="3" radius="md" border="border.subtle" testID={`app-command-${i}`}>
                  <Stack direction="horizontal" gap="2" align="end">
                    <Stack grow>
                      <TextField label={t("integrations.app.command")} value={c.command} onChange={(v) => vm.editCommand(i, { command: v })} testID={`app-command-${i}-name`} />
                    </Stack>
                    <IconButton icon="trash" accessibilityLabel={t("integrations.app.removeCommand")} onPress={() => vm.removeCommand(i)} testID={`app-command-${i}-remove`} />
                  </Stack>
                  <TextField label={t("integrations.app.commandUrl")} value={c.url} onChange={(v) => vm.editCommand(i, { url: v })} keyboard="url" testID={`app-command-${i}-url`} />
                  <TextField label={t("integrations.app.commandDescription")} value={c.description} onChange={(v) => vm.editCommand(i, { description: v })} testID={`app-command-${i}-description`} />
                  <TextField label={t("integrations.app.commandHint")} value={c.usageHint} onChange={(v) => vm.editCommand(i, { usageHint: v })} placeholder="[environment]" testID={`app-command-${i}-hint`} />
                </Stack>
              ))}
              <Stack direction="horizontal">
                <Button variant="ghost" size="sm" icon="plus" label={t("integrations.app.addCommand")} onPress={() => vm.addCommand()} testID="app-add-command" />
              </Stack>
            </Stack>
            <WorkflowStepsEditor steps={e.workflowSteps} onAdd={() => vm.addWorkflowStep()} onChange={(i, st) => vm.editWorkflowStep(i, st)} onRemove={(i) => vm.removeWorkflowStep(i)} />
            {e.problem === undefined ? null : (
              <Text variant="footnote" color="status.danger" testID="app-problem">
                {tr(e.problem)}
              </Text>
            )}
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" label={t("integrations.app.cancel")} onPress={() => vm.closeAppEditor()} testID="app-cancel" />
              <Button variant="primary" label={t(e.id === undefined ? "integrations.app.create" : "integrations.app.save")} loading={e.saving} onPress={() => void vm.saveApp()} testID="app-save" />
            </Stack>
          </Stack>
        )}
      </Sheet>

      <Sheet
        open={cred !== undefined}
        onClose={closeCred}
        title={cred === undefined ? "" : cred.reason === "created" ? t("integrations.app.credentialsTitle") : cred.reason === "signing" ? t("integrations.app.signingTitle") : t("integrations.app.secretTitle")}
        testID="app-credentials-sheet"
      >
        {cred === undefined ? null : (
          <Stack gap="3" padding="4">
            <Text color="text.secondary">
              {cred.reason === "created" ? t("integrations.app.credentialsBody") : cred.reason === "signing" ? t("integrations.app.signingBody") : t("integrations.app.secretBody")}
            </Text>
            {cred.items.map((item) => (
              <Stack key={item.kind} direction="horizontal" align="end" gap="2">
                <Stack grow>
                  <TextField label={credLabel(item.kind)} value={item.value} onChange={() => undefined} readOnly testID={`app-credential-${item.kind}`} />
                </Stack>
                <Button
                  variant="secondary"
                  icon="copy"
                  label={copied === item.kind ? t("integrations.app.copied") : t("integrations.app.copy")}
                  onPress={() => copy(item.kind, item.value)}
                  testID={`app-credential-${item.kind}-copy`}
                />
              </Stack>
            ))}
            <Stack direction="horizontal" justify="end">
              <Button variant="primary" label={t("integrations.app.done")} onPress={closeCred} testID="app-credentials-done" />
            </Stack>
          </Stack>
        )}
      </Sheet>

      <Sheet open={removing !== undefined} onClose={() => vm.cancelRemoveApp()} title={t("integrations.app.removeTitle")} testID="app-remove-sheet">
        {removing === undefined ? null : (
          <Stack gap="3" padding="4">
            <Text>{t("integrations.app.removeBody", { name: removing.app.name })}</Text>
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" label={t("common.cancel")} onPress={() => vm.cancelRemoveApp()} testID="app-remove-cancel" />
              <Button variant="destructive" label={t("integrations.app.remove")} onPress={() => vm.confirmRemoveApp()} testID="app-remove-confirm" />
            </Stack>
          </Stack>
        )}
      </Sheet>
    </>
  );
}

export function OutgoingSection({ vm, state: s }: { vm: IntegrationsViewModel; state: IntegrationsState }) {
  const t = useT();
  const tr = useTr();
  const { copied, copy } = useCopy();
  if (!s.appsSupported) return null;
  const e = s.outgoingEditor;
  const removing = s.outgoing.find((o) => o.webhook.id === s.confirmRemoveOutgoing);
  return (
    <>
      <SettingsSection title={t("integrations.outgoing.title")} description={t("integrations.outgoing.body")} testID="integrations-outgoing">
        {s.outgoing.length === 0 ? <FieldNote text={t("integrations.outgoing.empty")} testID="integrations-outgoing-empty" /> : null}
        <Stack gap="2">
          {s.outgoing.map(({ webhook: w, channels }) => {
            const menu: MenuEntry[] = [
              { id: "edit", label: t("integrations.outgoing.edit"), icon: "edit", onSelect: () => vm.openEditOutgoing(w.id) },
              { id: "regenerate", label: t("integrations.outgoing.regenerate"), icon: "reload", onSelect: () => vm.regenerateOutgoing(w.id) },
              { id: "sep", separator: true },
              { id: "remove", label: t("integrations.webhook.remove"), icon: "trash", destructive: true, onSelect: () => vm.askRemoveOutgoing(w.id) },
            ];
            const where = [
              channels.length === 0 ? t("integrations.outgoing.allPublic") : channels.join(", "),
              w.triggerWords.length === 0 ? undefined : t("integrations.outgoing.triggers", { words: w.triggerWords.join(", ") }),
            ]
              .filter((x): x is string => x !== undefined)
              .join(" · ");
            return (
              <Stack key={w.id} direction="horizontal" align="start" gap="3" paddingY="2" testID={`outgoing-${w.id}`}>
                <Icon name="send" size="icon.md" color="text.secondary" />
                <Stack grow gap="1">
                  <Text weight="semibold" lines={1}>
                    {w.name}
                  </Text>
                  <Text variant="footnote" color="text.secondary" lines={2}>
                    {t("integrations.outgoing.meta", { where, url: w.url })}
                  </Text>
                  {w.lastError === undefined ? null : (
                    <Text variant="footnote" color="status.danger" testID={`outgoing-${w.id}-error`}>
                      {t("integrations.outgoing.lastError", { error: w.lastError })}
                    </Text>
                  )}
                  <Stack direction="horizontal" align="center" gap="2">
                    <Text variant="caption" color="text.tertiary" lines={1}>
                      {t("integrations.outgoing.signingSecret")}
                    </Text>
                    <Text variant="caption" mono lines={1}>
                      {w.signingSecret}
                    </Text>
                    <IconButton
                      icon={copied === w.id ? "check" : "copy"}
                      accessibilityLabel={t("integrations.app.copy")}
                      onPress={() => copy(w.id, w.signingSecret)}
                      testID={`outgoing-${w.id}-copy`}
                    />
                  </Stack>
                </Stack>
                <Menu items={menu} align="end" testID={`outgoing-${w.id}-menu`}>
                  <IconButton icon="more" accessibilityLabel={t("integrations.webhook.more")} onPress={() => undefined} testID={`outgoing-${w.id}-more`} />
                </Menu>
              </Stack>
            );
          })}
        </Stack>
        {s.canAdd ? (
          <Stack direction="horizontal">
            <Button variant="secondary" icon="plus" label={t("integrations.outgoing.add")} onPress={() => vm.openAddOutgoing()} testID="integrations-add-outgoing" />
          </Stack>
        ) : null}
      </SettingsSection>

      <Sheet open={e !== undefined} onClose={() => vm.closeOutgoingEditor()} title={t(e?.id === undefined ? "integrations.outgoing.add" : "integrations.outgoing.edit")} testID="outgoing-sheet">
        {e === undefined ? null : (
          <Stack gap="3" padding="4">
            <TextField label={t("integrations.webhook.name")} value={e.name} onChange={(v) => vm.editOutgoing({ name: v })} autoFocus testID="outgoing-name" />
            <Stack gap="1">
              <Text variant="footnote" weight="semibold">
                {t("integrations.outgoing.channels")}
              </Text>
              <Text variant="caption" color="text.tertiary">
                {t("integrations.outgoing.channelsHelp")}
              </Text>
              {s.channels.map((c) => (
                <Checkbox key={c.id} label={c.label} value={e.channelIds.includes(c.id)} onChange={() => vm.toggleOutgoingChannel(c.id)} testID={`outgoing-channel-${c.id}`} />
              ))}
            </Stack>
            <TextField label={t("integrations.outgoing.triggerWords")} helperText={t("integrations.outgoing.triggerHelp")} value={e.triggerWords} onChange={(v) => vm.editOutgoing({ triggerWords: v })} testID="outgoing-triggers" />
            <TextField label={t("integrations.outgoing.url")} value={e.url} onChange={(v) => vm.editOutgoing({ url: v })} keyboard="url" placeholder="https://" testID="outgoing-url" />
            {e.problem === undefined ? null : (
              <Text variant="footnote" color="status.danger" testID="outgoing-problem">
                {tr(e.problem)}
              </Text>
            )}
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" label={t("common.cancel")} onPress={() => vm.closeOutgoingEditor()} testID="outgoing-cancel" />
              <Button variant="primary" label={t(e.id === undefined ? "integrations.webhook.create" : "common.save")} loading={e.saving} onPress={() => void vm.saveOutgoing()} testID="outgoing-save" />
            </Stack>
          </Stack>
        )}
      </Sheet>

      <Sheet open={removing !== undefined} onClose={() => vm.cancelRemoveOutgoing()} title={t("integrations.outgoing.removeTitle")} testID="outgoing-remove-sheet">
        {removing === undefined ? null : (
          <Stack gap="3" padding="4">
            <Text>{t("integrations.outgoing.removeBody", { name: removing.webhook.name })}</Text>
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" label={t("common.cancel")} onPress={() => vm.cancelRemoveOutgoing()} testID="outgoing-remove-cancel" />
              <Button variant="destructive" label={t("integrations.webhook.remove")} onPress={() => vm.confirmRemoveOutgoing()} testID="outgoing-remove-confirm" />
            </Stack>
          </Stack>
        )}
      </Sheet>
    </>
  );
}

/** Output / input names: lowercase letters, digits and _ (VariableKey). */
const keyOf = (v: string) => v.toLowerCase().replace(/[^a-z0-9_]/g, "_").slice(0, 40);

/** The app's workflow steps (Slack's functions): callback id, title, description, typed inputs and outputs. */
function WorkflowStepsEditor({ steps, onAdd, onChange, onRemove }: { steps: AppWorkflowStep[]; onAdd: () => void; onChange: (i: number, step: AppWorkflowStep) => void; onRemove: (i: number) => void }) {
  const t = useT();
  const types = WorkflowVarType.options.map((x) => ({ value: x, label: t(`workflow.varType.${x}`) }));
  return (
    <Stack gap="2" testID="app-steps">
      <Text variant="footnote" weight="semibold">
        {t("integrations.app.workflowSteps")}
      </Text>
      <Text variant="caption" color="text.secondary">
        {t("integrations.app.workflowStepsHelp")}
      </Text>
      {steps.map((st, i) => {
        const id = `app-step-${i}`;
        const set = (patch: Partial<AppWorkflowStep>) => onChange(i, { ...st, ...patch });
        return (
          <Stack key={i} gap="2" padding="3" radius="md" border="border.subtle" testID={id}>
            <Stack direction="horizontal" gap="2" align="end">
              <Stack grow>
                <TextField label={t("integrations.app.stepTitle")} value={st.title} onChange={(title) => set({ title: title.slice(0, 80) })} testID={`${id}-title`} />
              </Stack>
              <Stack grow>
                <TextField label={t("integrations.app.stepCallbackId")} value={st.callbackId} onChange={(callbackId) => set({ callbackId: callbackId.replace(/[^A-Za-z0-9_.-]/g, "_").slice(0, 64) })} testID={`${id}-callback`} />
              </Stack>
              <IconButton icon="trash" accessibilityLabel={t("integrations.app.removeStep")} onPress={() => onRemove(i)} testID={`${id}-remove`} />
            </Stack>
            <TextField label={t("integrations.app.stepDescription")} value={st.description ?? ""} onChange={(description) => set({ description: description.slice(0, 200) })} testID={`${id}-description`} />
            <Text variant="caption" weight="medium" color="text.secondary">
              {t("integrations.app.stepInputs")}
            </Text>
            {st.inputs.map((input, j) => {
              const setInput = (patch: Partial<AppWorkflowStep["inputs"][number]>) => set({ inputs: st.inputs.map((x, k) => (k === j ? { ...x, ...patch } : x)) });
              return (
                <Stack key={j} gap="2" padding="2" radius="md" background="bg.sunken" testID={`${id}-input-${j}`}>
                  <Stack direction="horizontal" gap="2">
                    <Stack grow>
                      <TextField label={t("integrations.app.stepParamLabel")} value={input.label} onChange={(label) => setInput({ label })} testID={`${id}-input-${j}-label`} />
                    </Stack>
                    <Stack grow>
                      <TextField label={t("integrations.app.stepParamName")} value={input.name} onChange={(name) => setInput({ name: keyOf(name) })} testID={`${id}-input-${j}-name`} />
                    </Stack>
                  </Stack>
                  <Stack direction="horizontal" gap="3" align="center">
                    <Select accessibilityLabel={t("integrations.app.stepParamType")} value={input.type} onChange={(type) => setInput({ type })} options={types} testID={`${id}-input-${j}-type`} />
                    <Stack grow>
                      <Checkbox label={t("integrations.app.stepParamRequired")} value={input.required === true} onChange={(required) => setInput({ required })} testID={`${id}-input-${j}-required`} />
                    </Stack>
                    <IconButton icon="trash" accessibilityLabel={t("integrations.app.removeParam")} onPress={() => set({ inputs: st.inputs.filter((_x, k) => k !== j) })} testID={`${id}-input-${j}-remove`} />
                  </Stack>
                </Stack>
              );
            })}
            <Stack direction="horizontal">
              <Button variant="ghost" size="sm" icon="plus" label={t("integrations.app.addStepInput")} onPress={() => set({ inputs: [...st.inputs, { name: `input${st.inputs.length + 1}`, label: "", type: "text" }] })} disabled={st.inputs.length >= MAX_APP_STEP_PARAMS} testID={`${id}-add-input`} />
            </Stack>
            <Text variant="caption" weight="medium" color="text.secondary">
              {t("integrations.app.stepOutputs")}
            </Text>
            {st.outputs.map((output, j) => (
              <Stack key={j} direction="horizontal" gap="2" align="center" testID={`${id}-output-${j}`}>
                <Stack grow>
                  <TextField accessibilityLabel={t("integrations.app.stepParamName")} placeholder={t("integrations.app.stepParamName")} value={output.name} onChange={(name) => set({ outputs: st.outputs.map((x, k) => (k === j ? { ...x, name: keyOf(name) } : x)) })} testID={`${id}-output-${j}-name`} />
                </Stack>
                <Select accessibilityLabel={t("integrations.app.stepParamType")} value={output.type} onChange={(type) => set({ outputs: st.outputs.map((x, k) => (k === j ? { ...x, type } : x)) })} options={types} testID={`${id}-output-${j}-type`} />
                <IconButton icon="trash" accessibilityLabel={t("integrations.app.removeParam")} onPress={() => set({ outputs: st.outputs.filter((_x, k) => k !== j) })} testID={`${id}-output-${j}-remove`} />
              </Stack>
            ))}
            <Stack direction="horizontal">
              <Button variant="ghost" size="sm" icon="plus" label={t("integrations.app.addStepOutput")} onPress={() => set({ outputs: [...st.outputs, { name: `output${st.outputs.length + 1}`, type: "text" }] })} disabled={st.outputs.length >= MAX_APP_STEP_PARAMS} testID={`${id}-add-output`} />
            </Stack>
          </Stack>
        );
      })}
      <Stack direction="horizontal">
        <Button variant="ghost" size="sm" icon="plus" label={t("integrations.app.addStep")} onPress={onAdd} disabled={steps.length >= MAX_APP_WORKFLOW_STEPS} testID="app-add-step" />
      </Stack>
    </Stack>
  );
}
